import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { EntityIcon } from "@/Components/Common/Icons";
import { getBadgeClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";

export type PillSize = "sm" | "xs";

export interface PillProps extends Omit<HTMLAttributes<HTMLSpanElement>, "color"> {
    /**
     * A semantic colour name — `success`, `danger`, `palette-4`, … — never a
     * Tailwind colour. `null` leaves the pill untinted for a caller that colours
     * it itself (the `Badge` variants do).
     */
    color?: string | null;
    /**
     * The icon before the label: an {@link EntityIcon} registry name, or a
     * node of its own when the icon has to move (a spinner, a pulsing dot).
     */
    icon?: string | ReactNode;
    /** A dashed outline instead of a fill — the "nothing set yet" form. */
    dashed?: boolean;
    /** `sm` (default) for tables and headers, `xs` for dense rows and cards. */
    size?: PillSize;
    children?: ReactNode;
}

const SIZE: Record<PillSize, string> = {
    sm: "h-6 gap-1.5 px-2.5 text-xs",
    xs: "h-5 gap-1 px-2 text-[11px]",
};

const ICON_SIZE: Record<PillSize, string> = {
    sm: "h-3 w-3",
    xs: "h-2.5 w-2.5",
};

/**
 * The app's label — the house style: a rounded, tinted pill with an optional
 * icon and a short word, in the sans face at medium weight. It is the shape a
 * document's status takes on every documents tab, and every other label takes
 * it too.
 *
 * Every label that names a state, a kind or a type — a document's status or
 * type, an invoice's kind, a payment status, a role, a tag — is drawn by this,
 * directly or through {@link Badge} / {@link StatusPill}, which both render a
 * Pill. Two labels side by side then read as the same kind of fact. Wrappers
 * decide the colour, icon and word; this decides the shape.
 */
export const Pill = forwardRef<HTMLSpanElement, PillProps>(function Pill(
    { color = "neutral", icon, dashed = false, size = "sm", className, children, ...props },
    ref,
) {
    return (
        <span
            ref={ref}
            className={cn(
                "inline-flex shrink-0 items-center whitespace-nowrap rounded-full font-sans font-medium",
                SIZE[size],
                dashed
                    ? "border border-dashed border-border text-muted-foreground"
                    : color !== null && getBadgeClasses(color),
                className,
            )}
            {...props}
        >
            {icon !== undefined && icon !== null && (
                <span className="relative flex shrink-0 items-center">
                    {typeof icon === "string" ? <EntityIcon name={icon} className={ICON_SIZE[size]} /> : icon}
                </span>
            )}
            {children}
        </span>
    );
});
